import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import {
  Moon,
  Sun,
  MousePointer,
  Lightbulb,
  Hand,
  Sparkles,
  GitBranch,
  LogOut,
  LayoutGrid,
  FileText,
  Zap,
  Network,
  Image,
  UserPlus,
  PanelRight,
  KeyRound,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAppStore } from '../stores/useAppStore'
import { useToast } from './ui/Toast'
import ChatPanel from './ChatPanel'
import DiagramViewer from './DiagramViewer'
import SddViewer from './SddViewer'
import InfiniteCanvas from './InfiniteCanvas'
import EventStormCanvas from './EventStormCanvas'
import PrototypeCanvas from './PrototypeCanvas'
import UserList from './UserList'
import IdeaPanel from './IdeaPanel'
import AgentTeamPanel from './AgentTeamPanel'
import AgentReasoningPanel from './AgentReasoningPanel'
import RegenerationConfirm from './RegenerationConfirm'
import type { UseCollaborationReturn } from '../hooks/useCollaboration'
import { useAgentObserver } from '../hooks/useAgentObserver'
import { useAgentRunState } from '../hooks/useAgentRunState'
import { normalizeAgentEventsList } from '../agui/agUiNormalization'
import type { AgentDiagram, HitlDecision } from '../agui/streamAgentTypes'
import CommandPalette, { type CommandItem } from './CommandPalette'
import DecisionDrawer from './DecisionDrawer'
import RoomFooter from './RoomFooter'
import ApiKeysModal from './ApiKeysModal'
import { PERSONAS, personaById } from '../lib/personas'
import type { PipelinePause } from '../lib/pipeline'
import { createInvitation, createRoom } from '../services/rooms'
import { STUDIO_IMPORT_KEY } from '../studio/importKey'
import { generateId } from '../lib/id'
import { ASK_FALLBACK, askPersona, chatIdentity, personaFromMention, type AskPersonaId } from '../lib/askAgent'
import type { CanvasAgentMark } from './InfiniteCanvas'
import type { ChatMessage } from '../types'
import BrandMark from './BrandMark'

interface LayoutShellProps {
  collaboration: UseCollaborationReturn | null
  roomId: string
}

export default function LayoutShell({ collaboration, roomId }: LayoutShellProps) {
  const navigate = useNavigate()
  const { currentRoom, user, darkMode, toggleDarkMode, activeTool, setActiveTool } = useAppStore()
  const [activePanel, setActivePanel] = useState<'canvas' | 'diagram' | 'sdd' | 'eventstorm' | 'prototype'>('canvas')
  const [rightPanelWidth, setRightPanelWidth] = useState(320)
  const [rightPanelOpen, setRightPanelOpen] = useState(true)
  const [isResizing, setIsResizing] = useState(false)
  const [agentStatus, setAgentStatus] = useState('')
  const [agentDiagrams, setAgentDiagrams] = useState<AgentDiagram[]>([])
  const [agentSdd, setAgentSdd] = useState('')
  const [agentPrototype, setAgentPrototype] = useState<Record<string, unknown> | null>(null)
  const [agentEvents, setAgentEvents] = useState<any[]>([])
  const [agentEventStorm, setAgentEventStorm] = useState<any[]>([])
  const [eventStormIsFallback, setEventStormIsFallback] = useState(false)
  // Erro de geração vindo do Yjs — visível também para quem não disparou o run
  const [remoteGenError, setRemoteGenError] = useState<string | null>(null)
  const [isReasoningOpen, setIsReasoningOpen] = useState(false)
  const [isTeamOpen, setIsTeamOpen] = useState(false)
  // Painéis colapsáveis da sidebar direita
  const [sideTab, setSideTab] = useState<'activity' | 'ideas' | 'people'>('activity')
  const [cmdOpen, setCmdOpen] = useState(false)
  const [decisionOpen, setDecisionOpen] = useState(false)
  const [agentPop, setAgentPop] = useState<string | null>(null)
  const [keysOpen, setKeysOpen] = useState(false)
  const [asking, setAsking] = useState(false)
  const [chatDraft, setChatDraft] = useState('')
  const [personaAsk, setPersonaAsk] = useState('')
  const askLock = useRef(false)
  const [pipelinePause, setPipelinePause] = useState<PipelinePause | null>(null)
  const [viewportWide, setViewportWide] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1280)
  const [nowTick, setNowTick] = useState(() => Date.now())
  const { toast } = useToast()
  const resizeStartX = useRef(0)
  const resizeStartWidth = useRef(0)

  const synced = collaboration?.synced ?? false
  const usersOnlineRaw = collaboration?.users ?? []
  const chatMessages = collaboration?.chatMessages
  const ideas = collaboration?.ideas
  const canvasElements = collaboration?.canvasElements
  const sendCursor = collaboration?.sendCursor
  const artifacts = collaboration?.artifacts
  const eventStormElements = collaboration?.eventStormElements
  // roomId vem de props

  const {
    regenerate,
    resumeAfterHitl,
    dismissError,
    isRunning: isAgentRunning,
    isRegenerating,
    error: agentError,
    lastRunStartedAt,
    isRemoteRunning,
    remoteRunStartedAt,
    hasPendingChanges,
  } = useAgentObserver(
    ideas,
    roomId,
    artifacts,
    (status) => {
      setAgentStatus(status)
    },
    eventStormElements,
    (events) => {
      // Immediate propagation — bypass Yjs observer latency/race conditions
      setAgentEvents(events)
    },
    collaboration?.isLeader ?? true,
    chatMessages,
  )

  const [confirmRegenerateOpen, setConfirmRegenerateOpen] = useState(false)
  const ideasCount = ideas?.length ?? 0
  const hasAnyArtifact = Boolean(
    agentDiagrams.length > 0 ||
      agentSdd ||
      agentPrototype ||
      agentEventStorm.length > 0,
  )

  // Hidrata Y.Array do Event Storm a partir do snapshot em artifacts (ex.: cliente entrou após o run do agente)
  useEffect(() => {
    if (!synced || !eventStormElements || !artifacts) return
    const fromArtifact = artifacts.get('event_storm')
    if (!Array.isArray(fromArtifact) || fromArtifact.length === 0) return
    if (eventStormElements.length > 0) return
    eventStormElements.insert(0, fromArtifact as Record<string, unknown>[])
  }, [synced, eventStormElements, artifacts])

  // O observer do Y.Map roda com closure antiga — lê o estado do stream local via ref.
  const localStreamActiveRef = useRef(false)
  localStreamActiveRef.current = isAgentRunning || isRegenerating

  // Observe shared artifacts from collaboration layer (for cross-user sync and initial load)
  useEffect(() => {
    if (!artifacts) return
    const handler = () => {
      // Hidratação de diagrams: prioriza a chave nova; cai em mermaid_c1..c4 para salas pré-refator.
      const dynamic = artifacts.get('diagrams')
      let nextDiagrams: AgentDiagram[]
      if (Array.isArray(dynamic) && dynamic.length > 0) {
        nextDiagrams = (dynamic as unknown[])
          .filter((d): d is { name: unknown; code: unknown } => !!d && typeof d === 'object')
          .map((d) => ({
            name: typeof d.name === 'string' ? d.name : '',
            code: typeof d.code === 'string' ? d.code : '',
            type:
              'type' in d && typeof (d as { type?: unknown }).type === 'string'
                ? (d as { type?: string }).type
                : null,
          }))
          .filter((d) => !!d.code.trim())
      } else {
        const legacy: Array<[string, unknown]> = [
          ['C1 Context', artifacts.get('mermaid_c1')],
          ['C2 Container', artifacts.get('mermaid_c2')],
          ['C3 Component', artifacts.get('mermaid_c3')],
          ['C4 Code', artifacts.get('mermaid_c4')],
        ]
        nextDiagrams = legacy
          .filter(([, code]) => typeof code === 'string' && code.trim() && !(code as string).startsWith('[ERRO]'))
          .map(([name, code]) => ({ name, code: code as string }))
      }
      setAgentDiagrams(nextDiagrams)
      setAgentSdd(String(artifacts.get('architecture') || ''))
      setAgentPrototype((artifacts.get('prototype') || null) as Record<string, unknown> | null)
      // NÃO sobrescrever eventos ao vivo do stream LOCAL com snapshot Yjs — causa perda de STEP_STARTED/STEP_FINISHED em tempo real.
      // Sem stream local: se outro participante está gerando, acompanha os `live_events`
      // que ele publica no Yjs (progresso ao vivo para toda a sala); senão usa o snapshot final.
      if (!localStreamActiveRef.current) {
        const liveEvents = artifacts.get('live_events')
        const generationStatus = artifacts.get('generation_status') as unknown
        if (generationStatus === 'running' && Array.isArray(liveEvents) && liveEvents.length > 0) {
          setAgentEvents(normalizeAgentEventsList(liveEvents))
        } else {
          const yjsEvents = normalizeAgentEventsList(artifacts.get('events') || [])
          setAgentEvents(yjsEvents)
        }
      }
      setAgentEventStorm((artifacts.get('event_storm') || []) as any[])
      setEventStormIsFallback(Boolean(artifacts.get('event_storm_is_fallback')))
      const pauseRaw = artifacts.get('pipeline_pause')
      setPipelinePause(
        pauseRaw && typeof pauseRaw === 'object' ? (pauseRaw as unknown as PipelinePause) : null,
      )
      const genError = artifacts.get('generation_error') as unknown
      setRemoteGenError(typeof genError === 'string' && genError ? genError : null)
    }
    artifacts.observe(handler)
    handler() // initial load
    return () => artifacts.unobserve(handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifacts])

  const hasSdd = Boolean(agentSdd)
  const hasPrototype = Boolean(agentPrototype)
  const hasManualEventStormEdits = useMemo(() => {
    const elements = (eventStormElements?.toArray() ?? agentEventStorm) as Array<{ created_by?: string }>
    return elements.some((el) => el.created_by && el.created_by !== 'agent')
  }, [eventStormElements, agentEventStorm])
  const hasDiagrams = agentDiagrams.length > 0
  // Ocupado local OU remoto: bloqueia regeneração e liga os overlays de progresso
  // também para quem não disparou o run.
  const isAgentBusy = isAgentRunning || isRegenerating || isRemoteRunning
  const isPaused = !isAgentBusy && Boolean(pipelinePause?.adrs?.length)
  const pendingAdr = pipelinePause?.adrs?.[0] ?? null

  /* ---------- Fonte de verdade centralizada AG-UI ---------- */
  const agentRunState = useAgentRunState(
    agentEvents,
    isAgentBusy,
    hasSdd,
    hasPrototype,
    hasDiagrams,
    agentStatus,
  )

  const {
    statuses: agentTeamStatus,
    peers: agentPeers,
    stepLabels: agentStepLabels,
    activeStep,
  } = agentRunState

  const usersOnline = [...usersOnlineRaw, ...agentPeers]

  const consumeChatDraft = useCallback(() => setChatDraft(''), [])

  const roomContext = useCallback(() => {
    const titles = (ideas?.toArray() ?? [])
      .map((idea) => idea.title)
      .filter(Boolean)
      .slice(0, 12)
      .join('; ')
    const sdd = String(artifacts?.get('architecture') || '').slice(0, 1200)
    return `Ideias: ${titles || 'nenhuma'}\nSDD: ${sdd || 'ainda não gerado'}`
  }, [ideas, artifacts])

  const pushAgentReply = useCallback(async (personaId: AskPersonaId, question: string) => {
    if (!chatMessages || askLock.current) return
    askLock.current = true
    setAsking(true)
    const identity = chatIdentity(personaId)
    let reply = ASK_FALLBACK[personaId]
    try {
      reply = await askPersona(personaId, question, roomContext())
    } catch {
      reply = ASK_FALLBACK[personaId]
    }
    const message: ChatMessage = {
      id: generateId(),
      content: reply,
      sender_id: identity.id,
      sender_name: identity.name,
      sender_type: 'agent',
      created_at: new Date().toISOString(),
    }
    chatMessages.push([message])
    askLock.current = false
    setAsking(false)
  }, [chatMessages, roomContext])

  const askFromPopover = useCallback((personaId: AskPersonaId, question: string) => {
    if (!chatMessages || !question.trim()) return
    const name = personaById(personaId)?.name || personaId
    const content = `@${name} ${question.trim()}`
    chatMessages.push([{
      id: generateId(),
      content,
      sender_id: user?.id || 'me',
      sender_name: user?.name || 'Você',
      sender_type: 'user',
      created_at: new Date().toISOString(),
    }])
    setSideTab('activity')
    setRightPanelOpen(true)
    setAgentPop(null)
    setPersonaAsk('')
    void pushAgentReply(personaId, content)
  }, [chatMessages, pushAgentReply, user?.id, user?.name])

  const requestSuggestions = useCallback(() => {
    if (!chatMessages) return
    chatMessages.push([{
      id: generateId(),
      content: 'Você pediu sugestões aos agentes',
      sender_id: 'system',
      sender_name: 'Sistema',
      sender_type: 'system',
      created_at: new Date().toISOString(),
    }])
    setSideTab('activity')
    setRightPanelOpen(true)
    const titles = (ideas?.toArray() ?? []).map((idea) => idea.title).filter(Boolean).slice(0, 12).join('; ')
    void pushAgentReply('dochie', `Olhando o canvas (${titles || 'ainda vazio'}), o que ainda falta?`)
  }, [chatMessages, ideas, pushAgentReply])

  const onChatSent = useCallback((content: string) => {
    const persona = personaFromMention(content)
    if (persona) void pushAgentReply(persona, content)
  }, [pushAgentReply])

  const agentMarks: CanvasAgentMark[] = useMemo(() => {
    if (ideasCount === 0) return []
    return [
      {
        id: 'dochie',
        name: 'Dochie',
        color: '#10b981',
        x: 520,
        y: 150,
        text: isAgentBusy
          ? (agentStepLabels.dochie?.label || 'Analisando o canvas.')
          : 'Posso apontar o que ainda falta no canvas.',
      },
      {
        id: 'archie',
        name: 'Archie',
        color: '#8b5cf6',
        x: 780,
        y: 260,
        text: isPaused
          ? 'Preciso de uma decisão antes de seguir.'
          : isAgentBusy
            ? (agentStepLabels.archie?.label || 'Consolidando o SDD.')
            : 'O SDD acompanha as ideias marcadas como prontas.',
      },
    ]
  }, [ideasCount, isAgentBusy, isPaused, agentStepLabels])

  const runStartedAt = isAgentRunning || isRegenerating ? lastRunStartedAt : remoteRunStartedAt

  // Toast de conclusão: quando o pipeline (local ou remoto) termina sem erro,
  // avisa e oferece atalho para o SDD — sem trocar de aba à força.
  const prevBusyRef = useRef(false)
  useEffect(() => {
    const wasBusy = prevBusyRef.current
    prevBusyRef.current = isAgentBusy
    // 'complete' no Yjs distingue sucesso de erro também para quem não disparou o run
    const finishedOk = (artifacts?.get('generation_status') as unknown) === 'complete'
    if (wasBusy && !isAgentBusy && !agentError && finishedOk) {
      toast({
        variant: 'success',
        title: 'Artefatos gerados',
        description: 'SDD, diagramas, Event Storm e protótipo foram atualizados.',
        action: { label: 'Ver SDD →', onClick: () => setActivePanel('sdd') },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAgentBusy])

  const handleRegenerateClick = useCallback(() => {
    if (isAgentBusy || ideasCount === 0) return
    if (hasAnyArtifact) {
      setConfirmRegenerateOpen(true)
    } else {
      regenerate()
    }
  }, [isAgentBusy, ideasCount, hasAnyArtifact, regenerate])

  // Um único gesto gera SDD, Event Storm, diagramas e protótipo.
  // Se o Archie pausou num ADR, o mesmo botão abre a decisão.
  const handlePrimaryAction = useCallback(() => {
    if (isPaused) {
      setDecisionOpen(true)
      return
    }
    if (isAgentBusy || ideasCount === 0) return
    handleRegenerateClick()
  }, [isPaused, isAgentBusy, ideasCount, handleRegenerateClick])

  const confirmRegenerate = useCallback(() => {
    setConfirmRegenerateOpen(false)
    regenerate()
  }, [regenerate])

  // Registra uma decisão HITL no Y.Map compartilhado — visível para toda a sala
  // e enviada ao backend (Archie) na próxima execução do pipeline.
  const registerHitlDecision = useCallback(
    (
      decision: HitlDecision['decision'],
      adr?: { adrId?: string | null; adrTitle?: string | null },
      feedback?: string,
    ) => {
      if (!artifacts) return
      const existing = artifacts.get('hitl_decisions')
      const list: HitlDecision[] = Array.isArray(existing) ? (existing as HitlDecision[]) : []
      const next: HitlDecision[] = [
        ...list,
        {
          adr_id: adr?.adrId ?? null,
          adr_title: adr?.adrTitle ?? null,
          decision,
          feedback: feedback ?? null,
          decided_by: user?.name ?? null,
          decided_at: new Date().toISOString(),
        },
      ]
      artifacts.set('hitl_decisions', next as unknown as Record<string, unknown>)
    },
    [artifacts, user],
  )

  const artifactCounts = useMemo(() => ({
    archie: agentSdd ? 1 : 0,
    dochie: agentSdd ? 1 : 0,
    jobs: agentEventStorm.length,
    vangogh: agentDiagrams.length + (agentPrototype ? 1 : 0),
  }), [agentDiagrams, agentSdd, agentEventStorm, agentPrototype])

  // Cada persona leva ao artefato que ela realmente produz:
  // Archie escreve o SDD; Dochie alimenta as análises embutidas no SDD;
  // Jobs modela o Event Storm; Van Gogh entrega diagramas (e protótipo).
  const handleAgentClick = useCallback((agentId: string) => {
    setIsTeamOpen(false)
    switch (agentId) {
      case 'archie':
      case 'dochie':
        setActivePanel('sdd')
        break
      case 'jobs':
        setActivePanel('eventstorm')
        break
      case 'vangogh':
        setActivePanel(hasDiagrams || !agentPrototype ? 'diagram' : 'prototype')
        break
    }
  }, [hasDiagrams, agentPrototype])

  useEffect(() => {
    if (!isAgentBusy) return
    const id = window.setInterval(() => setNowTick(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [isAgentBusy])

  useEffect(() => {
    const onResize = () => setViewportWide(window.innerWidth >= 1280)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setCmdOpen((open) => !open)
      }
      if (e.key === 'Escape') {
        setCmdOpen(false)
        setDecisionOpen(false)
        setAgentPop(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('decision') === '1' && isPaused) setDecisionOpen(true)
  }, [isPaused])

  const elapsed = (() => {
    if (!isAgentBusy || !runStartedAt) return ''
    const start = new Date(runStartedAt).getTime()
    if (Number.isNaN(start)) return ''
    const total = Math.max(0, Math.floor((nowTick - start) / 1000))
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
  })()

  // Cria um projeto Studio no mesmo workspace com o diagrama do Van Gogh, editável.
  const openDiagramInStudio = useCallback(
    async (diagram: AgentDiagram) => {
      const workspaceId = (currentRoom as { workspace_id?: string } | null)?.workspace_id
      if (!workspaceId) return
      try {
        const name = `${currentRoom?.name || 'Projeto'} · ${diagram.name || 'Diagrama'}`.slice(0, 200)
        const room = await createRoom({
          workspace_id: workspaceId,
          name,
          description: `Diagrama editável criado a partir de "${currentRoom?.name || 'projeto'}".`,
          room_type: 'studio',
        })
        sessionStorage.setItem(STUDIO_IMPORT_KEY(room.id), JSON.stringify({ code: diagram.code, name: diagram.name }))
        const workspaceName = (currentRoom as { workspace_name?: string | null } | null)?.workspace_name
        const next = { ...room, my_role: 'owner', workspace_name: workspaceName }
        localStorage.setItem('currentRoom', JSON.stringify(next))
        useAppStore.setState({ currentRoom: next })
        navigate(`/?room=${room.id}`)
      } catch (err) {
        const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        toast({ variant: 'error', title: 'Não foi possível abrir no Studio', description: detail })
      }
    },
    [currentRoom, navigate, toast],
  )

  const handleInvite = useCallback(async () => {
    if (!currentRoom?.id) return
    try {
      const invitation = await createInvitation(currentRoom.id)
      const url = `${window.location.origin}/join/${invitation.token}`
      await navigator.clipboard.writeText(url)
      toast({
        variant: 'success',
        title: 'Link de convite copiado',
        description: 'Válido por 7 dias. Quem entrar vê as ideias, o chat e os agentes.',
      })
    } catch {
      toast({ variant: 'error', title: 'Não foi possível criar o convite' })
    }
  }, [currentRoom?.id, toast])

  const submitDecision = useCallback(
    (decision: HitlDecision['decision'], feedback?: string) => {
      if (!pendingAdr) return
      registerHitlDecision(decision, { adrId: pendingAdr.adr_id, adrTitle: pendingAdr.adr_title }, feedback)
      setDecisionOpen(false)
      setAgentPop(null)
      resumeAfterHitl()
    },
    [pendingAdr, registerHitlDecision, resumeAfterHitl],
  )

  useEffect(() => {
    const stored = localStorage.getItem('darkMode')
    if (stored === 'true') {
      document.documentElement.classList.add('dark')
      useAppStore.setState({ darkMode: true })
    }
  }, [])

  useEffect(() => {
    if (!collaboration) return
    const unsub = () => {
      // cleanup if needed
    }
    return unsub
  }, [collaboration])

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      setIsResizing(true)
      resizeStartX.current = e.clientX
      resizeStartWidth.current = rightPanelWidth
    },
    [rightPanelWidth]
  )

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!isResizing) return
      const delta = resizeStartX.current - e.clientX
      const newWidth = Math.max(240, Math.min(480, resizeStartWidth.current + delta))
      setRightPanelWidth(newWidth)
    }
    const handleUp = () => setIsResizing(false)
    if (isResizing) {
      window.addEventListener('mousemove', handleMove)
      window.addEventListener('mouseup', handleUp)
    }
    return () => {
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('mouseup', handleUp)
    }
  }, [isResizing])

  const flowTabs = [
    { id: 'canvas' as const, label: 'Ideias', icon: Lightbulb, meta: String(ideasCount) },
    { id: 'eventstorm' as const, label: 'Event Storm', icon: Zap, meta: agentEventStorm.length ? String(agentEventStorm.length) : '' },
    { id: 'sdd' as const, label: 'SDD', icon: FileText, meta: hasSdd ? 'v' : '' },
    { id: 'diagram' as const, label: 'Diagramas', icon: Network, meta: agentDiagrams.length ? String(agentDiagrams.length) : '' },
    { id: 'prototype' as const, label: 'Protótipo', icon: Image, meta: agentPrototype ? '1' : '' },
  ]

  const commands: CommandItem[] = [
    { id: 'gen', label: 'Gerar / atualizar documentação', hint: 'Agentes', icon: Sparkles, run: handlePrimaryAction },
    { id: 'hitl', label: pendingAdr?.adr_id ? `Revisar decisão · ${pendingAdr.adr_id}` : 'Revisar decisão pendente', hint: 'HITL', icon: GitBranch, run: () => isPaused && setDecisionOpen(true) },
    { id: 'idea', label: 'Nova ideia no canvas', hint: 'N', icon: Lightbulb, run: () => { setActivePanel('canvas'); setActiveTool('idea') } },
    { id: 'suggest', label: 'Pedir sugestões aos agentes', hint: 'Leve', icon: Sparkles, run: requestSuggestions },
    { id: 'canvas', label: 'Ir para Ideias', icon: Lightbulb, run: () => setActivePanel('canvas') },
    { id: 'storm', label: 'Ir para Event Storm', icon: Zap, run: () => setActivePanel('eventstorm') },
    { id: 'sdd', label: 'Ir para SDD', icon: FileText, run: () => setActivePanel('sdd') },
    { id: 'diagram', label: 'Ir para Diagramas', icon: Network, run: () => setActivePanel('diagram') },
    { id: 'proto', label: 'Ir para Protótipo', icon: Image, run: () => setActivePanel('prototype') },
    { id: 'dash', label: 'Todos os projetos', icon: LayoutGrid, run: () => navigate('/dashboard') },
    { id: 'keys', label: 'Chaves de API do projeto', hint: 'MCP', icon: KeyRound, run: () => setKeysOpen(true) },
    { id: 'theme', label: 'Alternar tema claro/escuro', icon: darkMode ? Sun : Moon, run: toggleDarkMode },
    {
      id: 'logout',
      label: 'Sair da conta',
      icon: LogOut,
      run: () => {
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        window.location.href = '/login'
      },
    },
  ]

  const runError =
    agentError && !agentError.includes('tentar de novo automaticamente')
      ? agentError
      : !isAgentBusy
        ? remoteGenError
        : null

  const openPersona = agentPop ? personaById(agentPop) : undefined
  const humans = usersOnlineRaw.filter((u) => !u.id.startsWith('agent-')).slice(0, viewportWide ? 4 : 2)

  return (
    <div className="relative flex flex-col h-screen bg-canvas text-ink">
      <header className="h-12 shrink-0 flex items-center gap-2.5 px-3 border-b border-line bg-surface">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          title="Projetos"
          aria-label="Voltar aos projetos"
          className="w-7 h-7 rounded-md flex items-center justify-center text-ink hover:bg-surface-2 cursor-pointer shrink-0"
        >
          <BrandMark size={22} />
        </button>
        <div className="min-w-0 flex items-center gap-1.5 text-sm">
          {viewportWide && (currentRoom as { workspace_name?: string | null } | null)?.workspace_name && (
            <span className="text-faint whitespace-nowrap">{(currentRoom as { workspace_name?: string | null }).workspace_name} /</span>
          )}
          <span className="font-semibold truncate">{currentRoom?.name || 'Sala'}</span>
        </div>
        <div className="flex-1" />
        <nav className="flex items-center gap-0.5 bg-surface-2 border border-line rounded-lg p-0.5">
          {flowTabs.map((tab, index) => {
            const active = activePanel === tab.id
            const generating = isAgentBusy && (
              (tab.id === 'sdd' && activeStep === 'archie.synthesis') ||
              (tab.id === 'eventstorm' && activeStep === 'output.parallel') ||
              (tab.id === 'diagram' && activeStep === 'output.parallel') ||
              (tab.id === 'prototype' && activeStep === 'output.parallel')
            )
            const stale = hasPendingChanges && tab.id !== 'canvas' && !generating
            return (
              <span key={tab.id} className="flex items-center">
                {index === 1 && <span className="text-faint px-0.5">›</span>}
                <button
                  type="button"
                  onClick={() => setActivePanel(tab.id)}
                  className={`h-7 flex items-center gap-1.5 px-2.5 rounded-md text-[12.5px] font-medium cursor-pointer whitespace-nowrap ${
                    active ? 'bg-surface border border-line text-ink' : 'text-muted'
                  }`}
                >
                  <tab.icon size={13} />
                  {(viewportWide || active) && tab.label}
                  {viewportWide && tab.meta && <span className="font-mono text-[11px] text-faint">{tab.meta}</span>}
                  {(generating || stale) && (
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: generating ? 'var(--af-acc)' : 'var(--af-warn)', animation: generating ? 'afpulse 1s infinite' : undefined }}
                    />
                  )}
                </button>
              </span>
            )
          })}
        </nav>
        <div className="flex-1" />
        {isPaused && pendingAdr && (
          <button
            type="button"
            onClick={() => setDecisionOpen(true)}
            className="h-7 flex items-center gap-1.5 px-2 rounded-lg border border-warn-line bg-warn-soft text-warn text-xs font-semibold cursor-pointer"
          >
            <span className="w-2 h-2 rounded-full bg-warn" style={{ animation: 'afpulse 1.4s ease-in-out infinite' }} />
            {viewportWide && 'Decisão pendente'}
            <span className="font-mono text-[11px] opacity-80">{pendingAdr.adr_id}</span>
          </button>
        )}
        <div className="flex items-center pl-1">
          {humans.map((person) => (
            <div
              key={person.id}
              title={person.name}
              className="w-6 h-6 rounded-full text-white text-[10px] font-semibold flex items-center justify-center border-2 border-surface -ml-1"
              style={{ background: person.color || '#3b82f6' }}
            >
              {person.name.charAt(0).toUpperCase()}
            </div>
          ))}
          {PERSONAS.map((persona) => (
            <button
              key={persona.id}
              type="button"
              title={`${persona.name} — ${agentStepLabels[persona.id]?.label || persona.role}`}
              onClick={() => setAgentPop((current) => (current === persona.id ? null : persona.id))}
              className="w-6 h-6 rounded-md text-white flex items-center justify-center border-2 border-surface -ml-1 cursor-pointer"
              style={{ background: persona.color }}
            >
              <persona.icon size={11} />
            </button>
          ))}
        </div>
        <button type="button" onClick={() => setCmdOpen(true)} title="Comandos (⌘K)" className="h-7 px-2 rounded-md border border-line text-muted font-mono text-[11px] cursor-pointer">
          ⌘K
        </button>
        <button type="button" onClick={toggleDarkMode} title="Alternar tema" className="w-7 h-7 rounded-md border border-line text-muted cursor-pointer" aria-label={darkMode ? 'Ativar modo claro' : 'Ativar modo escuro'}>
          {darkMode ? <Sun size={14} className="mx-auto" /> : <Moon size={14} className="mx-auto" />}
        </button>
        <button type="button" onClick={() => setKeysOpen(true)} title="Chaves de API" className="w-7 h-7 rounded-md border border-line text-muted cursor-pointer" aria-label="Chaves de API">
          <KeyRound size={14} className="mx-auto" />
        </button>
        <button type="button" onClick={handleInvite} className="h-7 px-2.5 rounded-md bg-ink text-surface text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer">
          <UserPlus size={13} />
          {viewportWide && 'Convidar'}
        </button>
        <button
          type="button"
          onClick={() => setRightPanelOpen((open) => !open)}
          title={rightPanelOpen ? 'Fechar painel' : 'Abrir painel'}
          className="w-7 h-7 rounded-md border border-line text-muted cursor-pointer"
          style={{ background: rightPanelOpen ? 'var(--af-sf2)' : 'var(--af-sf)' }}
        >
          <PanelRight size={14} className="mx-auto" />
        </button>
      </header>

      <main className="flex-1 flex overflow-hidden min-h-0">
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex-1 relative min-h-0">
            {activePanel === 'canvas' ? (
              ideas && canvasElements ? (
                <InfiniteCanvas
                  ideas={ideas}
                  canvasElements={canvasElements}
                  users={usersOnline}
                  activeTool={activeTool}
                  currentUserId={user?.id}
                  agentStatus={agentStatus}
                  onCursorMove={sendCursor}
                  agentMarks={agentMarks}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-faint">Conectando ao canvas...</div>
              )
            ) : activePanel === 'diagram' ? (
              <DiagramViewer
                diagrams={agentDiagrams}
                agentStatus={agentStatus}
                darkMode={darkMode}
                isRegenerating={isAgentBusy}
                onOpenInStudio={openDiagramInStudio}
              />
            ) : activePanel === 'sdd' ? (
              <SddViewer
                markdown={agentSdd}
                agentStatus={agentStatus}
                darkMode={darkMode}
                isRegenerating={isAgentBusy}
                stale={hasPendingChanges}
                onReviewAdr={() => isPaused && setDecisionOpen(true)}
                onOpenDiagrams={() => setActivePanel('diagram')}
              />
            ) : activePanel === 'eventstorm' ? (
              <EventStormCanvas
                elements={eventStormElements ?? agentEventStorm}
                users={usersOnline}
                currentUserId={user?.id}
                isRegenerating={isAgentBusy}
                isFallback={eventStormIsFallback}
                onRegenerate={handleRegenerateClick}
                onDiscuss={(text) => {
                  setChatDraft(`@Jobs vamos discutir: ${text}`)
                  setSideTab('activity')
                  setRightPanelOpen(true)
                }}
              />
            ) : (
              <PrototypeCanvas
                data={agentPrototype as never}
                darkMode={darkMode}
                isRegenerating={isAgentBusy}
                onRegenerate={handleRegenerateClick}
              />
            )}

            {activePanel === 'canvas' && (
              <div className="absolute left-3 top-3 flex flex-col gap-0.5 bg-surface border border-line rounded-lg p-0.5 shadow-sm">
                {([
                  ['select', 'Selecionar (V)', MousePointer],
                  ['idea', 'Nova ideia', Lightbulb],
                  ['hand', 'Mover', Hand],
                ] as const).map(([id, label, Icon]) => (
                  <button
                    key={id}
                    type="button"
                    title={label}
                    onClick={() => setActiveTool(id)}
                    className={`w-7 h-7 rounded-md cursor-pointer ${activeTool === id ? 'bg-accent-soft text-accent' : 'text-muted hover:bg-surface-2'}`}
                  >
                    <Icon size={14} className="mx-auto" />
                  </button>
                ))}
                <div className="h-px bg-line mx-1" />
                <button type="button" title="Pedir sugestões aos agentes" onClick={requestSuggestions} className="w-7 h-7 rounded-md text-accent cursor-pointer hover:bg-accent-soft">
                  <Sparkles size={14} className="mx-auto" />
                </button>
              </div>
            )}
            {activePanel === 'canvas' && (
              <div className="absolute right-3 top-3 bg-surface border border-line rounded-lg px-2.5 py-1 text-xs text-muted shadow-sm">
                <b className="text-ink font-semibold">{ideasCount}</b> ideias
                <span className="mx-1.5 text-line">|</span>
                <span className={hasPendingChanges ? 'text-warn' : ''}>
                  {hasPendingChanges ? 'novas desde a última geração' : 'em dia com a documentação'}
                </span>
              </div>
            )}

            {openPersona && (
              <div className="absolute left-1/2 bottom-3 -translate-x-1/2 w-[420px] max-w-[calc(100%-24px)] bg-surface border border-line rounded-xl shadow-xl z-10 overflow-hidden">
                <div className="flex items-center gap-2.5 px-3.5 py-3 border-b border-line">
                  <div className="w-9 h-9 rounded-lg text-white flex items-center justify-center" style={{ background: openPersona.color }}>
                    <openPersona.icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{openPersona.name}</div>
                    <div className="text-xs text-muted">{openPersona.role}</div>
                  </div>
                  <button type="button" onClick={() => setAgentPop(null)} className="text-muted text-xs cursor-pointer">fechar</button>
                </div>
                <div className="px-3.5 py-3 text-[12.5px] leading-relaxed bg-surface-2 border border-line-2 rounded-lg m-3">
                  {agentStepLabels[openPersona.id]?.label || 'Disponível'}
                </div>
                <form
                  className="px-3.5 pb-2 flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (openPersona) askFromPopover(openPersona.id, personaAsk)
                  }}
                >
                  <input
                    value={personaAsk}
                    onChange={(e) => setPersonaAsk(e.target.value)}
                    placeholder={`Perguntar a ${openPersona.name}`}
                    aria-label={`Perguntar a ${openPersona.name}`}
                    className="flex-1 h-8 px-2.5 rounded-md border border-line bg-canvas text-xs outline-none focus:ring-2 focus:ring-accent"
                  />
                  <button type="submit" className="h-8 px-2.5 rounded-md bg-ink text-surface text-xs font-semibold cursor-pointer">
                    Enviar
                  </button>
                </form>
                <div className="px-3.5 pb-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      handleAgentClick(openPersona.id)
                      setAgentPop(null)
                    }}
                    className="h-7 px-2.5 rounded-md border border-line text-xs cursor-pointer"
                  >
                    Abrir artefato
                  </button>
                  {isPaused && openPersona.id === 'archie' && (
                    <button type="button" onClick={() => { setDecisionOpen(true); setAgentPop(null) }} className="h-7 px-2.5 rounded-md bg-warn text-white text-xs font-semibold cursor-pointer">
                      Revisar decisão
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          <RoomFooter
            events={agentEvents}
            running={isAgentBusy}
            paused={isPaused}
            pendingChanges={hasPendingChanges}
            newIdeas={0}
            hasArtifacts={hasAnyArtifact}
            ideasCount={ideasCount}
            statuses={agentTeamStatus}
            stepLabels={agentStepLabels}
            elapsed={elapsed}
            error={runError}
            onRetry={() => { dismissError(); regenerate() }}
            onDismissError={dismissError}
            onPrimary={handlePrimaryAction}
            onOpenAgent={(id) => setAgentPop((current) => (current === id ? null : id))}
            openAgent={agentPop}
          />
        </div>

        {rightPanelOpen && (
          <>
            <div className={`resizer shrink-0 ${isResizing ? 'resizing' : ''}`} onMouseDown={handleResizeStart} />
            <aside className="shrink-0 border-l border-line bg-surface flex flex-col min-h-0" style={{ width: rightPanelWidth }}>
              <div className="h-10 shrink-0 flex items-stretch gap-0.5 px-2 border-b border-line">
                {([
                  ['activity', 'Atividade', ''],
                  ['ideas', 'Ideias', String(ideasCount)],
                  ['people', 'Pessoas', String(usersOnline.length)],
                ] as const).map(([id, label, count]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setSideTab(id)}
                    className={`px-2.5 text-[12.5px] font-medium cursor-pointer border-b-2 ${
                      sideTab === id ? 'border-ink text-ink' : 'border-transparent text-muted'
                    }`}
                  >
                    {label} {count && <span className="font-mono text-[11px] text-faint">{count}</span>}
                  </button>
                ))}
              </div>
              {sideTab === 'activity' && (
                <div className="flex-1 min-h-0 flex flex-col">
                  {isPaused && pendingAdr && (
                    <div className="m-2 p-2.5 rounded-lg border border-warn-line bg-warn-soft flex flex-col gap-2">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-warn">Archie precisa de você</div>
                      <div className="text-[13px] font-semibold">{pendingAdr.adr_id} · {pendingAdr.adr_title}</div>
                      <button type="button" onClick={() => setDecisionOpen(true)} className="self-start h-7 px-2.5 rounded-md bg-ink text-surface text-xs font-semibold cursor-pointer">
                        Comparar opções
                      </button>
                    </div>
                  )}
                  {chatMessages ? (
                    <ChatPanel
                      chatMessages={chatMessages}
                      currentUser={user}
                      onSent={onChatSent}
                      draft={chatDraft}
                      onDraftConsumed={consumeChatDraft}
                      asking={asking}
                    />
                  ) : (
                    <div className="flex-1 flex items-center justify-center text-faint text-sm">Conectando ao chat...</div>
                  )}
                </div>
              )}
              {sideTab === 'ideas' && (
                <div className="flex-1 min-h-0 flex flex-col">
                  {ideas ? <IdeaPanel ideas={ideas} /> : <div className="p-4 text-sm text-faint">Conectando...</div>}
                </div>
              )}
              {sideTab === 'people' && (
                <div className="flex-1 min-h-0 overflow-auto">
                  <UserList users={usersOnline} />
                </div>
              )}
            </aside>
          </>
        )}
      </main>

      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} commands={commands} />
      <ApiKeysModal open={keysOpen} roomId={roomId} onClose={() => setKeysOpen(false)} />
      <DecisionDrawer
        open={decisionOpen}
        adr={pendingAdr}
        reviews={pipelinePause?.reviews || []}
        onClose={() => setDecisionOpen(false)}
        onApprove={(feedback, optionName) => {
          const note = [optionName ? `Opção: ${optionName}` : '', feedback].filter(Boolean).join('. ')
          submitDecision('approved', note || undefined)
        }}
        onReject={(feedback) => submitDecision('rejected', feedback || undefined)}
      />
      <RegenerationConfirm
        open={confirmRegenerateOpen}
        ideasCount={ideasCount}
        hasArtifacts={hasAnyArtifact}
        hasManualEventStormEdits={hasManualEventStormEdits}
        onCancel={() => setConfirmRegenerateOpen(false)}
        onConfirm={confirmRegenerate}
      />
      <AgentReasoningPanel
        events={agentEvents}
        isOpen={isReasoningOpen}
        onClose={() => setIsReasoningOpen(false)}
        onHitlApprove={(adr) => {
          registerHitlDecision('approved', adr)
          resumeAfterHitl()
        }}
        onHitlReject={(adr) => {
          registerHitlDecision('rejected', adr)
          resumeAfterHitl()
        }}
        onHitlOverride={(feedback, adr) => {
          registerHitlDecision('note', adr, feedback)
          toast({
            variant: 'info',
            title: 'Observação registrada',
            description: 'Ela entra na próxima síntese. Aprovar ou rejeitar o ADR retoma o pipeline.',
          })
        }}
      />
      <AgentTeamPanel
        statuses={agentTeamStatus}
        onAgentClick={handleAgentClick}
        onClose={() => setIsTeamOpen(false)}
        artifactCounts={artifactCounts}
        isOpen={isTeamOpen}
        stepLabels={agentStepLabels}
      />
    </div>
  )
}

